import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { hourLabel } from '../sms/format';
import type { AnalyticsRange, TextAnalyticsView, WeeklyByMethod } from './types';
import { formatMinutes, percent, rateText, usd } from './format';
import styles from './analytics.module.css';

export function AnalyticsCard({
  title,
  sub,
  aside,
  children,
}: {
  title: string;
  sub?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>{title}</h3>
          {sub && <p className={styles.cardSub}>{sub}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Kpi({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={styles.kpi}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={accent ? styles.kpiValueAccent : styles.kpiValue}>{value}</div>
      {sub && <div className={styles.kpiSub}>{sub}</div>}
    </div>
  );
}

/** "▲ 4 pts" / "▼ 12%" against the previous period. */
export function Delta({
  current,
  previous,
  unit = 'pts',
}: {
  current: number | null;
  previous: number | null;
  unit?: 'pts' | 'count';
}) {
  if (current === null || previous === null || current === previous) return null;
  const up = current > previous;
  const amount =
    unit === 'pts'
      ? `${Math.abs(Math.round(current - previous))} pts`
      : `${Math.abs(current - previous)}`;
  return <span className={up ? styles.up : styles.down}>{`${up ? '▲' : '▼'} ${amount}`}</span>;
}

export function RangeSwitch({
  value,
  onChange,
}: {
  value: AnalyticsRange;
  onChange: (r: AnalyticsRange) => void;
}) {
  return (
    <div className={styles.seg} role="group" aria-label="Date range">
      {(['7d', '30d', '90d'] as const).map((r) => (
        <button
          key={r}
          type="button"
          className={value === r ? styles.segButtonActive : styles.segButton}
          aria-pressed={value === r}
          onClick={() => onChange(r)}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function HBar({
  label,
  value,
  max,
  display,
  tone = 'accent',
}: {
  label: ReactNode;
  value: number;
  max: number;
  display: string;
  tone?: 'accent' | 'success' | 'warning';
}) {
  const width = max > 0 ? Math.round((value / max) * 100) : 0;
  const barClass =
    tone === 'success' ? styles.barSuccess : tone === 'warning' ? styles.barWarning : styles.bar;
  return (
    <div className={styles.hbar}>
      <span>{label}</span>
      <span className={styles.track}>
        <span className={barClass} style={{ width: `${width}%` }} />
      </span>
      <span className={styles.num}>{display}</span>
    </div>
  );
}

export function RatingMixCard({ view }: { view: TextAnalyticsView }) {
  const rows = (['5', '4', '3', '2', '1'] as const).map((r) => [r, view.ratingMix[r]] as const);
  const total = rows.reduce((a, [, n]) => a + n, 0);
  return (
    <AnalyticsCard title="Rating mix" sub="Replies 1–5">
      {total === 0 ? (
        <p className={styles.muted}>No ratings yet.</p>
      ) : (
        <div className={styles.hbars}>
          {rows.map(([r, n]) => (
            <HBar key={r} label={'★'.repeat(Number(r))} value={n} max={total} display={String(n)} />
          ))}
        </div>
      )}
    </AnalyticsCard>
  );
}

export function ReplyByHourCard({ view }: { view: TextAnalyticsView }) {
  const rows = view.byHour.filter((h) => h.sent > 0);
  return (
    <AnalyticsCard
      title="Reply rate by send hour"
      sub="Patient’s local time · texts sent in brackets"
    >
      {rows.length === 0 ? (
        <p className={styles.muted}>No texts in this range yet.</p>
      ) : (
        <div className={styles.hbars}>
          {rows.map((h) => (
            <HBar
              key={h.hour}
              label={`${hourLabel(h.hour)} (${h.sent})`}
              value={h.replied}
              max={h.sent}
              display={percent(h.replied, h.sent)}
            />
          ))}
        </div>
      )}
    </AnalyticsCard>
  );
}

export function ByDelayCard({ view }: { view: TextAnalyticsView }) {
  const rows = view.byDelay.filter((d) => d.sent > 0);
  return (
    <AnalyticsCard
      title="Reply rate by time since the visit"
      sub="Same-day texts get the most replies; catching up later still works"
    >
      {rows.length === 0 ? (
        <p className={styles.muted}>No texts in this range yet.</p>
      ) : (
        <div className={styles.hbars}>
          {rows.map((d) => (
            <HBar
              key={d.bucket}
              label={`${d.label} (${d.sent})`}
              value={d.replied}
              max={d.sent}
              display={percent(d.replied, d.sent)}
            />
          ))}
        </div>
      )}
    </AnalyticsCard>
  );
}

export function ByProviderCard({ view }: { view: TextAnalyticsView }) {
  return (
    <AnalyticsCard title="By provider" sub="Who the visit was with">
      {view.byProvider.length === 0 ? (
        <p className={styles.muted}>No texts in this range yet.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Provider</th>
                <th className={styles.right}>Texted</th>
                <th className={styles.right}>Replied</th>
                <th className={styles.right}>On Google</th>
                <th className={styles.right}>Private</th>
              </tr>
            </thead>
            <tbody>
              {view.byProvider.map((p) => (
                <tr key={p.provider}>
                  <td>{p.provider}</td>
                  <td className={styles.right}>{p.texted}</td>
                  <td className={styles.right}>
                    {p.replied} ({percent(p.replied, p.texted)})
                  </td>
                  <td className={styles.right}>{p.matched}</td>
                  <td className={styles.right}>{p.privateFeedback}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AnalyticsCard>
  );
}

export function WeeklyByMethodCard({
  weekly,
  title = 'Google reviews per week',
  sub = 'New reviews matched to a request, by how the patient was asked',
}: {
  weekly: WeeklyByMethod[];
  title?: string;
  sub?: string;
}) {
  const max = Math.max(1, ...weekly.map((w) => w.text + w.qr));
  const any = weekly.some((w) => w.text + w.qr > 0);
  return (
    <AnalyticsCard
      title={title}
      sub={sub}
      aside={
        <div className={styles.legend}>
          <span>
            <span className={styles.swatchText} />
            From texts
          </span>
          <span>
            <span className={styles.swatchQr} />
            From link &amp; QR
          </span>
        </div>
      }
    >
      {!any ? (
        <p className={styles.muted}>
          Reviews show here once they appear on Google and are matched to a request (checked with
          each daily Google sync).
        </p>
      ) : (
        <>
          <div className={styles.stacked}>
            {weekly.map((w) => (
              <div
                key={w.weekStart}
                className={styles.week}
                title={`Week of ${w.weekStart}: ${w.text} from texts, ${w.qr} from QR`}
              >
                <span className={styles.weekText} style={{ height: `${(w.text / max) * 100}%` }} />
                <span className={styles.weekQr} style={{ height: `${(w.qr / max) * 100}%` }} />
              </div>
            ))}
          </div>
          <div className={styles.axis}>
            <span>{weekly[0]?.weekStart}</span>
            <span>This week</span>
          </div>
        </>
      )}
    </AnalyticsCard>
  );
}

export function DeliveryCard({ view }: { view: TextAnalyticsView }) {
  const d = view.delivery;
  const healthy =
    (d.deliveredRate === null || d.deliveredRate >= 95) &&
    (d.optOutRate === null || d.optOutRate <= 3);
  return (
    <AnalyticsCard
      title="Delivery & compliance"
      sub="Carrier results and opt-outs"
      aside={
        <span className={healthy ? styles.pill_ok : styles.pill_warn}>
          {healthy ? 'Healthy' : 'Check'}
        </span>
      }
    >
      <div className={styles.hbars}>
        <HBar
          label="Delivered"
          value={d.deliveredRate ?? 0}
          max={100}
          display={rateText(d.deliveredRate)}
          tone="success"
        />
        <HBar
          label="Opted out (STOP)"
          value={d.optOutRate ?? 0}
          max={100}
          display={d.optOutRate === null ? '—' : `${d.optOutRate}%`}
          tone="warning"
        />
        {d.remindersSent !== null && (
          <HBar
            label="Reminder sent"
            value={d.remindersSent}
            max={Math.max(1, view.kpis.texted)}
            display={String(d.remindersSent)}
          />
        )}
        {d.repliedAfterReminder !== null && (
          <HBar
            label="Replied after reminder"
            value={d.repliedAfterReminder}
            max={Math.max(1, d.remindersSent ?? 0)}
            display={String(d.repliedAfterReminder)}
          />
        )}
      </div>
      {d.failuresByCode.length > 0 && (
        <p className={styles.note}>
          Failures by carrier code:{' '}
          {d.failuresByCode.map((f) => `${f.code} (${f.count})`).join(' · ')}
        </p>
      )}
    </AnalyticsCard>
  );
}

export function StaffCard({ view }: { view: TextAnalyticsView }) {
  const s = view.staff;
  return (
    <AnalyticsCard
      title="Needed a person"
      sub="Handed to staff by Betty, and whether they followed up"
    >
      {s.byKind.length === 0 && !s.takeovers ? (
        <p className={styles.muted}>Nothing was handed to staff in this range.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Why</th>
                <th className={styles.right}>Threads</th>
                <th className={styles.right}>Followed up</th>
              </tr>
            </thead>
            <tbody>
              {s.byKind.map((k) => (
                <tr key={k.kind}>
                  <td>{k.label}</td>
                  <td className={styles.right}>{k.threads}</td>
                  <td className={styles.right}>{k.followedUp}</td>
                </tr>
              ))}
              {s.takeovers !== null && (
                <tr>
                  <td>Staff took over a thread</td>
                  <td className={styles.right}>{s.takeovers}</td>
                  <td className={styles.right}>–</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {s.medianMinutesToFollowUp !== null && (
        <p className={styles.note}>
          Median time to follow up: {formatMinutes(s.medianMinutesToFollowUp)}
        </p>
      )}
    </AnalyticsCard>
  );
}

export function CostCard({ view }: { view: TextAnalyticsView }) {
  const c = view.cost;
  return (
    <AnalyticsCard title="Cost" sub="What texting this practice costs PracticeNova (estimated)">
      {!c ? (
        <p className={styles.muted}>
          Cost needs Betty’s conversation records, which couldn’t be reached right now.
        </p>
      ) : (
        <>
          <dl className={styles.dl}>
            <dt>
              Texts ({c.outboundSegments} sent segments, {c.inboundMessages} received)
            </dt>
            <dd>{usd(c.textsUsd)}</dd>
            <dt>Betty’s replies (AI)</dt>
            <dd>{usd(c.repliesAiUsd)}</dd>
            <dt>Review drafts (AI)</dt>
            <dd>{usd(c.draftsAiUsd)}</dd>
            <dt>Number and carrier registration (share)</dt>
            <dd>{usd(c.numberUsd)}</dd>
            <dt className={styles.dtStrong}>Per review live on Google</dt>
            <dd className={styles.ddAccent}>
              {c.perReviewUsd === null ? '—' : usd(c.perReviewUsd)}
            </dd>
          </dl>
          <p className={styles.note}>
            Text prices are estimates (Telnyx sends no cost per message).
          </p>
        </>
      )}
    </AnalyticsCard>
  );
}

export function RecentConversationsCard({
  view,
  onOpen,
}: {
  view: TextAnalyticsView;
  onOpen?: (shortCode: string) => void;
}) {
  return (
    <AnalyticsCard
      title="Recent conversations"
      sub="The latest patients texted and where each ended"
    >
      {view.recent.length === 0 ? (
        <p className={styles.muted}>No texts in this range yet.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Patient</th>
                <th>Provider</th>
                <th>Rating</th>
                <th>Where it ended</th>
                <th>First text</th>
              </tr>
            </thead>
            <tbody>
              {view.recent.map((r) => (
                <tr
                  key={r.shortCode}
                  className={clsx(onOpen && styles.rowLink)}
                  onClick={onOpen ? () => onOpen(r.shortCode) : undefined}
                >
                  <td>{r.patient}</td>
                  <td>{r.providerName ?? '—'}</td>
                  <td>{r.rating ? `${r.rating}★` : '–'}</td>
                  <td>
                    <span className={styles[`pill_${r.tone}`]}>{r.ended}</span>
                  </td>
                  <td>
                    {new Date(r.firstTextAt).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AnalyticsCard>
  );
}
